import React, { useMemo, useState } from "react";
import { PlayCircle, Loader, ChevronDown, ChevronRight } from "lucide-react";
import TotalGapSummary from "./TotalGapSummary";
import BufferSettings from "./BufferSettings";
import SuggestedActions from "./SuggestedActions";
import TopUpsPanel from "./TopUpsPanel";
import LiabilityCards from "./LiabilityCards";
import PositionExplainPanel from "./PositionExplainPanel";
import { formatCurrencyAmount } from "../utils/formatCurrencyAmount";
import { useDailyPosition, useRunPositionCheck, useHeldBreakdown, useTreasuryThresholds } from "../hooks/useTreasury";

const formatAmount = (value) => {
  const num = Number(value);
  return isNaN(num) ? "0.00" : num.toLocaleString(undefined, { minimumFractionDigits: 2 });
};

const formatRate = (value) => {
  const num = Number(value);
  return isNaN(num) ? "0.00" : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 });
};

// Reads the computed held-breakdown endpoint, not raw partner_balances -
// a USD-only partner's (e.g. Cashramp's) share of a currency is a computed
// allocation, never its own partner_balances row, so a breakdown built
// from that table alone would never add up to the Held figure above it.
function PartnerBreakdown({ currency }) {
  const { data, isLoading } = useHeldBreakdown(currency);

  if (isLoading) {
    return <span className="text-sm text-gray-400">Loading partner breakdown…</span>;
  }

  if (!data || data.length === 0) {
    return <span className="text-sm text-gray-400">No partner balances recorded for {currency} yet.</span>;
  }

  return (
    <div className="flex flex-wrap gap-6">
      {data.map((entry) => (
        <div key={entry.partner} className="text-sm">
          <span className="capitalize text-gray-500">{entry.partner?.toLowerCase()}: </span>
          <span className="font-medium text-gray-900">{formatCurrencyAmount(entry.amount, currency)}</span>
          {entry.isAllocated && (
            <span
              className="ml-1 text-xs text-gray-400"
              title="Computed proportional share of this partner's USD balance - not its own recorded balance in this currency"
            >
              (allocated from USD
              {entry.allocationRate != null && (
                <> @ {formatRate(entry.allocationRate)}{entry.allocationRateSource ? ` via ${entry.allocationRateSource}` : ""}</>
              )}
              )
            </span>
          )}
          {entry.notEnabledForPayouts && (
            <div className="mt-0.5">
              <span
                className="inline-block px-2 py-0.5 text-xs font-medium text-amber-800 bg-amber-50 rounded-full border border-amber-100"
                title={`${entry.partner} is not enabled for payouts in ${currency} (every corridor off, or this currency is not linked). The money is still counted as held, but cannot pay out ${currency} payouts.`}
              >
                not enabled for {currency} payouts
              </span>
            </div>
          )}
          {entry.isUsdWalletOnly && (
            <div className="mt-0.5">
              <span
                className="inline-block px-2 py-0.5 text-xs font-medium text-amber-800 bg-amber-50 rounded-full border border-amber-100"
                title={`This partner holds only a USD wallet. $${formatAmount(entry.allocatedUsd)} of it is allocated to ${currency}${entry.surplusUsd > 0 ? `, including $${formatAmount(entry.surplusUsd)} of surplus USD` : ""}.`}
              >
                USD wallet only partner to {currency}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Pure Merchant <-> Partner reconciliation - does merchant liability match
// what partners actually hold. Consumer and the house-merchant pass-
// through wallet are deliberately NOT blended into this figure (consumer's
// claim is already represented via whichever pass-through wallet relayed
// its deposit, not a second independent claim) - see
// ConsumerMerchantPositionTable for that separate, self-contained check.
function MerchantPartnerPositionTable() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const { data, isLoading, isError, error } = useDailyPosition(date);
  const { mutate: runCheck, isPending: isRunning } = useRunPositionCheck();
  const [expandedCurrency, setExpandedCurrency] = useState(null);
  const { data: thresholds } = useTreasuryThresholds();
  const defaultBufferPct = thresholds?.defaultBufferPct ?? 2;
  const bufferByCurrency = new Map((thresholds?.overrides ?? []).map((o) => [o.currency, o.minBufferPct]));

  // red: held below owed. amber: covered, but inside the safety buffer.
  const statusFor = (row) => {
    const owed = Number(row.owedAmount);
    const held = Number(row.heldAmount);
    if (owed <= 0) return "ok";
    if (held < owed) return "red";
    const pct = bufferByCurrency.get(row.currency) ?? defaultBufferPct;
    return held < owed * (1 + pct / 100) ? "amber" : "ok";
  };

  const byCurrency = useMemo(() => {
    const grouped = new Map();
    for (const row of data ?? []) {
      if (row.level !== "combined") continue;
      grouped.set(row.currency, row);
    }
    return Array.from(grouped.values());
  }, [data]);

  if (isError) {
    return (
      <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">
        Error: {error.message}
      </p>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-3 justify-between items-start pb-4 md:flex-row md:items-center">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="px-4 py-2 text-sm rounded-3xl border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        />
        <button
          onClick={() => runCheck()}
          disabled={isRunning}
          className="flex gap-2 items-center px-4 py-2 text-sm font-bold text-white bg-[#013941] rounded-3xl transition-all hover:bg-[#012126] disabled:opacity-50"
        >
          <PlayCircle className={`w-4 h-4 ${isRunning ? "animate-spin" : ""}`} />
          Run position check now
        </button>
      </div>

      <LiabilityCards date={date} show={["merchant"]} />
      {!isLoading && byCurrency.length > 0 && <TotalGapSummary rows={byCurrency} />}
      {!isLoading && byCurrency.length > 0 && <SuggestedActions />}
      {!isLoading && <TopUpsPanel />}
      {!isLoading && byCurrency.length > 0 && (
        <BufferSettings currencies={byCurrency.map((r) => r.currency).sort()} />
      )}

      {isLoading ? (
        <div className="flex h-[300px]">
          <div className="flex flex-1 justify-center items-center">
            <p className="flex gap-2 items-center font-medium text-gray-600">
              Loading daily position <Loader className="w-5 h-5 animate-spin" />
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/50">
                  <th className="w-8 border-b"></th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6">Currency</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Merchant Owed</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Owed (USD)</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Held</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Gap</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Gap (USD)</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {byCurrency.length > 0 ? (
                  byCurrency
                    .slice()
                    .sort((a, b) => Number(a.gapAmountUsd) - Number(b.gapAmountUsd))
                    .map((row) => {
                      const status = statusFor(row);
                      const isShort = status === "red";
                      const isWarn = status === "amber";
                      const isExpanded = expandedCurrency === row.currency;

                      return (
                        <React.Fragment key={row.currency}>
                          <tr
                            className="cursor-pointer transition-colors hover:bg-gray-50"
                            onClick={() => setExpandedCurrency(isExpanded ? null : row.currency)}
                          >
                            <td className="px-2 text-center text-gray-400">
                              {isExpanded ? <ChevronDown className="inline w-4 h-4" /> : <ChevronRight className="inline w-4 h-4" />}
                            </td>
                            <td className="px-4 py-3 font-sans text-sm font-medium text-gray-900 md:px-6 md:py-5">{row.currency}</td>
                            <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">{formatCurrencyAmount(row.owedAmount, row.currency)}</td>
                            <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">{row.owedAmountUsd == null ? "-" : formatAmount(row.owedAmountUsd)}</td>
                            <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">
                              {formatCurrencyAmount(row.heldAmount, row.currency)}
                              {Number(row.heldAtInactiveAmount) > 0.005 && (
                                <span
                                  className="block text-xs font-medium text-amber-700"
                                  title="Still ours and counted in Held, but at a partner that is not enabled for payouts in this currency, so it cannot pay out here. Suggestions and automatic conversion do not count it."
                                >
                                  {formatCurrencyAmount(row.heldAtInactiveAmount, row.currency)} at partners not enabled
                                </span>
                              )}
                            </td>
                            <td className={`px-4 py-3 font-sans text-sm text-right md:px-6 md:py-5 ${isShort ? "text-red-600" : isWarn ? "text-amber-600" : "text-green-700"}`}>
                              {formatCurrencyAmount(row.gapAmount, row.currency)}
                            </td>
                            <td className={`px-4 py-3 font-sans text-sm font-semibold text-right md:px-6 md:py-5 ${isShort ? "text-red-600" : isWarn ? "text-amber-600" : "text-green-700"}`}>
                              {formatAmount(row.gapAmountUsd)}
                            </td>
                            <td className="px-4 py-3 font-sans text-sm md:px-6 md:py-5">
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-medium ${isShort ? "bg-red-100 text-red-800" : isWarn ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"
                                  }`}
                                title={isWarn ? "Covered, but inside the safety buffer" : undefined}
                              >
                                {isShort ? "Shortfall" : isWarn ? "Low buffer" : "Covered"}
                              </span>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-gray-50/70">
                              <td></td>
                              <td colSpan={7} className="px-6 py-4">
                                <div className="text-xs font-bold tracking-wider text-gray-500 uppercase">
                                  Partner balance breakdown - what's actually behind the "Held" figure above
                                </div>
                                <div className="mt-2">
                                  <PartnerBreakdown currency={row.currency} />
                                </div>
                                <PositionExplainPanel currency={row.currency} date={date} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                ) : (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                      No position computed for this date yet - click "Run position check now".
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default MerchantPartnerPositionTable;
